import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useFamily } from 'src/clients/hooks/useFamily';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  UnifiedDocument,
  useAllClientDocuments,
} from 'src/suitability-report/hooks/useAllClientDocuments';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import FileUploadLayout from 'src/data-capture/file-management/FileUploadLayout';
import { useDeleteGeneratedDocument } from 'src/data-capture/file-management/hooks/useDeleteGeneratedDocument';
import { useDownloadDocumentFile } from 'src/data-capture/file-management/hooks/useDownloadDocumentFile';
import EmptyView from 'src/data-capture/file-management/components/EmptyView';
import GenerateDocumentsControl from 'src/data-capture/file-management/components/GenerateDocumentsControl';
import GeneratedDocumentInlineActions from 'src/data-capture/file-management/components/GeneratedDocumentInlineActions';
import SelectionHeaderActions from 'src/data-capture/file-management/components/SelectionHeaderActions';
import { buildGeneratedDocActions } from 'src/data-capture/file-management/components/generatedDocActions';
import { useBulkDocumentSelection } from 'src/data-capture/file-management/hooks/useBulkDocumentSelection';
import BulkDeleteConfirmModal from 'src/data-capture/file-management/components/BulkDeleteConfirmModal';
import {
  GeneratedDocumentType,
  GENERATED_DOCUMENT_TYPE_LABELS,
  GENERATED_DOCUMENT_TYPE_LABELS_PLURAL,
  matchesGeneratedDocumentSearch,
} from 'src/data-capture/file-management/components/generatedDocumentTypes';
import { type GeneratedDocument } from 'src/data-capture/file-management/hooks/useGeneratedDocuments';
import FilesTable from 'src/data-capture/file-management/files-table/FilesTable';
import {
  generatedDocumentCreatedColumn,
  generatedDocumentStatusColumn,
  generatedDocumentTypeColumn,
  toGeneratedDocumentRow,
  type GeneratedDocumentRow,
} from 'src/data-capture/file-management/files-table/generatedDocumentColumns';

type DocumentToDelete = {
  id: string;
  clientId: string;
  displayName: string;
  documentType: GeneratedDocumentType;
};

type FamilyGeneratedDocumentsProps = {
  documentTypeFilter?: GeneratedDocumentType;
};

const GENERATED_TYPES: GeneratedDocumentType[] = [
  'suitability-report',
  'meeting-agenda',
  'data-export',
  'meeting-note',
];

export default function FamilyGeneratedDocuments({
  documentTypeFilter,
}: FamilyGeneratedDocumentsProps) {
  const { familyId = '' } = useParams<{ familyId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const flags = useFlags();

  const { family } = useFamily(familyId);
  const familyName = family?.name;

  const contextClientId = searchParams.get('member') ?? family?.mainFamilyMember.clientId ?? null;

  const {
    documents: unifiedDocuments,
    isLoading,
    error,
    refetch,
  } = useAllClientDocuments({
    type: 'family',
    familyId,
  });
  const {
    deleteDocument,
    deleteErrorFor,
    resetErrors,
    isDeleting: isDeletingDocument,
  } = useDeleteGeneratedDocument();
  const { downloadFile, downloadFileViaAnchor } = useDownloadDocumentFile({ familyId });

  const [searchValue, setSearchValue] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentToDelete | null>(null);

  const openFlow = (documentType: GeneratedDocumentType) => {
    if (!contextClientId) return;
    navigate(`/client/${contextClientId}/${documentType}`, {
      state: { fromPath: location.pathname },
    });
  };

  const handleDeletePress = (document: DocumentToDelete) => {
    resetErrors();
    setDocumentToDelete(document);
  };

  const deleteError = documentToDelete ? deleteErrorFor(documentToDelete.documentType) : null;

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    const { clientId, id, documentType } = documentToDelete;
    try {
      await deleteDocument(clientId, id, documentType);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete document',
        'An error occurred while trying to delete the document. Please try again later.',
        e,
        { familyId, clientId, documentId: id, documentType },
      );
    }
    setDocumentToDelete(null);
    await refetch();
  };

  const title = documentTypeFilter
    ? GENERATED_DOCUMENT_TYPE_LABELS_PLURAL[documentTypeFilter]
    : 'Generated documents';

  const documents: GeneratedDocument[] = unifiedDocuments
    .filter((doc): doc is UnifiedDocument & { documentType: GeneratedDocumentType } =>
      GENERATED_TYPES.includes(doc.documentType as GeneratedDocumentType),
    )
    .map(toGeneratedDocument)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const typedDocuments = documentTypeFilter
    ? documents.filter(doc => doc.documentType === documentTypeFilter)
    : documents;

  const filteredDocuments = searchValue.trim()
    ? typedDocuments.filter(doc => matchesGeneratedDocumentSearch(doc, searchValue))
    : typedDocuments;

  const additionalColumns = documentTypeFilter
    ? [generatedDocumentCreatedColumn, generatedDocumentStatusColumn]
    : [generatedDocumentCreatedColumn, generatedDocumentTypeColumn];

  const renderGenerateControl = (buttonText: string) => (
    <GenerateDocumentsControl
      documentTypeFilter={documentTypeFilter}
      openFlow={openFlow}
      buttonText={buttonText}
    />
  );

  const rows = filteredDocuments.map(toGeneratedDocumentRow);

  const {
    selection,
    selectedRecords: selectedRows,
    bulkDeleteNames,
    requestBulkDelete,
    cancelBulkDelete,
    confirmBulkDelete,
    isBulkDeleting,
    isBulkDownloading,
    downloadSelected,
  } = useBulkDocumentSelection({
    records: rows,
    entityName: 'document',
    nameOf: row => row.document.displayName,
    deleteRecord: row =>
      deleteDocument(row.document.clientId, row.document.id, row.document.documentType),
    downloadRecord: row => {
      if (row.document.fileId == null) return;
      return downloadFileViaAnchor(row.document.fileId, row.document.displayName);
    },
    refetch,
    logContext: { familyId },
  });
  const hasDownloadableSelection = selectedRows.some(row => row.document.fileId != null);

  const renderInlineActions = (row: GeneratedDocumentRow) => (
    <GeneratedDocumentInlineActions
      doc={row.document}
      onDownload={downloadFile}
      showGeneratingIndicator={!documentTypeFilter}
    />
  );

  if (error) {
    return <GenericErrorPage error={error} />;
  }

  if (typedDocuments.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title={title} familyId={familyId} familyName={familyName} variant="compact">
        <EmptyView
          title="Your AI-generated documents will appear here"
          subtitle="Generate suitability reports, meeting agendas, and data exports with Siesta AI. All your AI-created documents will be organised in one convenient location."
          customCta={renderGenerateControl('Generate new')}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title={selection.isSelecting ? 'Select the files' : title}
      familyId={familyId}
      familyName={familyName}
      variant="compact"
      collapsibleSearch={!selection.isSelecting}
      searchOpen={searchOpen}
      onSearchOpenChange={setSearchOpen}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchResultCount={filteredDocuments.length}
      searchPlaceholder="Search documents"
      actions={
        selection.isSelecting ? (
          <SelectionHeaderActions
            selectedCount={selectedRows.length}
            onDeleteSelected={requestBulkDelete}
            onDownloadSelected={downloadSelected}
            onCancel={selection.exit}
            isDownloading={isBulkDownloading}
            downloadDisabled={!hasDownloadableSelection}
          />
        ) : (
          renderGenerateControl('Generate')
        )
      }
    >
      <FilesTable<GeneratedDocumentRow>
        entitiesWithFile={rows}
        isLoading={isLoading}
        additionalColumns={additionalColumns}
        nameSubtitle={() => undefined}
        inlineActions={renderInlineActions}
        actions={row =>
          buildGeneratedDocActions({
            doc: row.document,
            clientId: row.document.clientId,
            navigate,
            flags,
            includeEdit: false,
            onSelect: () => selection.enter(row.id),
            onDelete: () =>
              handleDeletePress({
                id: row.document.id,
                clientId: row.document.clientId,
                displayName: row.document.displayName,
                documentType: row.document.documentType,
              }),
          })
        }
        variant="compact"
        nameColumnTitle="File name"
        selection={
          selection.isSelecting
            ? {
                selectedIds: selection.selectedIds,
                onToggle: selection.toggle,
              }
            : undefined
        }
      />

      <ConfirmDeleteModal
        opened={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete document"
        message={`Are you sure you want to delete "${documentToDelete?.displayName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeletingDocument}
      />

      <BulkDeleteConfirmModal
        fileNames={bulkDeleteNames}
        entityName="document"
        onClose={cancelBulkDelete}
        onConfirm={confirmBulkDelete}
        isLoading={isBulkDeleting}
      />
    </FileUploadLayout>
  );
}

function toGeneratedDocument(
  doc: UnifiedDocument & { documentType: GeneratedDocumentType },
): GeneratedDocument {
  return {
    id: doc.id,
    clientId: doc.clientId,
    documentType: doc.documentType,
    createdAt: doc.createdAt,
    fileId: doc.fileId,
    file: doc.file,
    backgroundProcess: doc.backgroundProcess,
    // Renamable only when the shown name is the file's own name; when it falls back
    // to a config or type label (see documentDisplayName) there is nothing to rename.
    renameFileId: doc.file?.fileName ? doc.fileId : undefined,
    displayName: documentDisplayName(doc),
  };
}

function documentDisplayName(
  doc: UnifiedDocument & { documentType: GeneratedDocumentType },
): string {
  if (doc.file?.fileName) return doc.file.fileName;
  if (doc.documentType === 'data-export' && doc.configDisplayName) return doc.configDisplayName;
  return GENERATED_DOCUMENT_TYPE_LABELS[doc.documentType];
}
